import React from 'react';
import {
  StackLayout,
  StackItem,
  UtilityText,
  Button,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import type { BulkActionMenuItem } from '@web-config-app/core-react';
import { SelectActionMenu } from '../../select-action-menu/select-action-menu.component';

export interface EntityListTableBulkActionsProps {
  /** Array of bulk actions to display in the dropdown menu. */
  bulkActions: BulkActionMenuItem[];
  /** Number of currently selected items to display */
  selectedCount: number;
  /** Whether the bulk actions bar is visible. Controls animated show/hide. */
  isVisible: boolean;
}

/**
 * A toolbar that appears above the entity list table when items are selected in multi-select mode.
 * Displays the count of selected items and an "Actions" dropdown menu.
 */
export const EntityListTableBulkActions: React.FC<
  EntityListTableBulkActionsProps
> = ({ bulkActions, selectedCount, isVisible }) => {
  const { formatMessage } = useIntl();

  const menuGroups = React.useMemo(
    () => [
      {
        menuItems: bulkActions.map((bulkAction) => ({
          id: bulkAction.id,
          label: bulkAction.label,
          action: bulkAction.action ? () => bulkAction.action?.() : undefined,
          disabledTooltip: bulkAction.disabledTooltip,
          confirmationModal: bulkAction.confirmationModal,
          keepOpenOnSelect: bulkAction.keepOpenOnSelect,
        })),
      },
    ],
    [bulkActions],
  );

  return (
    <StackLayout
      css={{
        paddingY: isVisible ? '$half' : 0,
        paddingX: '$half',
        backgroundColor: '$surfaceBackgroundSecondary',
        maxHeight: isVisible ? 48 : 0,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        transitionBehavior: 'allow-discrete',
      }}
      orientation="horizontal"
      horizontalAlignment="stretch"
    >
      <StackItem horizontalAlignment="fillContainer" verticalAlignment="center">
        <UtilityText>
          {formatMessage(
            {
              id: 'BULK_ITEMS_SELECTED',
            },
            { count: selectedCount },
          )}
        </UtilityText>
      </StackItem>
      {bulkActions.length > 0 && (
        <StackItem horizontalAlignment="end">
          <SelectActionMenu menuGroups={menuGroups}>
            <Button priority="secondary" size="toolbar">
              {formatMessage({ id: 'BULK_ACTIONS' })}
            </Button>
          </SelectActionMenu>
        </StackItem>
      )}
    </StackLayout>
  );
};
